import React, {useCallback, useEffect, useState} from 'react';
import {Chip, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import ConfirmationNumberIcon from '@material-ui/icons/ConfirmationNumber';
import FingerprintIcon from '@material-ui/icons/Fingerprint';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import {useSnackbar} from 'notistack';
import API_Service from '../../../../Services/API_Service';
import {req_module_setting} from '../../../../Helper/Constants';
import {getSetting} from '../../../../Helper/Helper';

const useStyles = makeStyles((theme) => ({
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {gridTemplateColumns: '1fr'}
    },
    card: {
        position: 'relative',
        minHeight: 152,
        padding: theme.spacing(2.25),
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 14,
        backgroundColor: theme.palette.background.paper,
        transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
        '&:hover': {
            transform: 'translateY(-3px)',
            borderColor: theme.palette.primary.main,
            boxShadow: theme.palette.type === 'dark' ? '0 12px 28px rgba(0,0,0,.22)' : '0 12px 28px rgba(38,42,51,.09)'
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            right: -24,
            bottom: -28,
            width: 90,
            height: 90,
            borderRadius: '50%',
            backgroundColor: theme.palette.type === 'dark' ? 'rgba(254,242,0,.06)' : 'rgba(254,242,0,.12)'
        }
    },
    icon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 38,
        height: 38,
        marginBottom: theme.spacing(2),
        borderRadius: 11,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: theme.palette.primary.main,
        '& svg': {fontSize: 20}
    },
    label: {color: theme.palette.text.secondary, fontSize: 12, fontWeight: 700},
    value: {marginTop: theme.spacing(0.75), fontSize: 24, fontWeight: 800, letterSpacing: '-0.025em'},
    format: {display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: theme.spacing(1)},
    separator: {color: theme.palette.text.secondary, fontWeight: 700},
    chip: {fontWeight: 700}
}));

export default function OutletCodeSection({axiosConfig, language}) {
    const classes = useStyles();
    const {enqueueSnackbar} = useSnackbar();
    const [settings, setSettings] = useState(null);

    const load = useCallback(() => {
        setSettings(null);
        API_Service.get(req_module_setting, axiosConfig).then(({data}) => {
            if (data.result) setSettings(getSetting(data.extraData, 'settings', {}));
            else {
                setSettings({});
                enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
            }
        }).catch(() => {
            setSettings({});
            enqueueSnackbar(language.operation_fail || 'Unable to load settings', {variant: 'error'});
        });
    }, [axiosConfig, enqueueSnackbar, language.operation_fail]);

    useEffect(() => { load(); }, [load]);

    const values = settings || {};
    const format = values.ref_format && values.ref_format.outlet
        ? String(values.ref_format.outlet).replace(/{{(.*?)}}/g, (_, match) => match).split('-')
        : [];

    const cards = [
        {key: 'running', label: language.next_running_no || 'Next running no.', value: values.running_no && values.running_no.outlet, icon: <FormatListNumberedIcon/>},
        {key: 'reference', label: language.reference_no || 'Reference no.', value: values.ref_no && values.ref_no.outlet, icon: <FingerprintIcon/>},
        {key: 'format', label: language.code_format || 'Code format', value: format, icon: <ConfirmationNumberIcon/>}
    ];

    return (
        <div className={classes.grid}>
            {cards.map((card) => (
                <article className={classes.card} key={card.key}>
                    <span className={classes.icon} aria-hidden="true">{card.icon}</span>
                    <Typography className={classes.label}>{card.label}</Typography>
                    {settings === null ? <Skeleton animation="wave" height={42}/> : card.key === 'format' ? (
                        <div className={classes.format}>
                            {card.value.length ? card.value.map((part, index) => (
                                <React.Fragment key={`${part}-${index}`}>
                                    <Chip className={classes.chip} size="small" label={part}/>
                                    {index < card.value.length - 1 ? <span className={classes.separator}>—</span> : null}
                                </React.Fragment>
                            )) : <Typography className={classes.value}>—</Typography>}
                        </div>
                    ) : <Typography className={classes.value}>{card.value || '—'}</Typography>}
                </article>
            ))}
        </div>
    );
}
